import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Modal } from '@mui/material';
import { Key, ArrowLeft, Loader, Plus, Trash2, CheckCircle2, Shield, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertTriangle, Calendar, Search, RefreshCw, ChevronUp, ChevronDown, CircleEllipsis, Pencil } from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { showError, showSuccess, showInfo } from '../../../utils/toast';
import { hasPermission } from '../../../utils/permissionsHelper';
import { jwt_decode } from '../../../utils/jwt_decode';
import CredentialForm from './CredentialForm';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import { useClientLevel } from '../../../hooks/useClientLevel';

export interface CredentialData {
    id?: string;
    credential_name: string;
    ip_addresses?: any
    credential_type: 'snmp' | 'firewall' | 'vmware' | 'camera';
    enabled: boolean;
    is_active?: boolean;

    // SNMP fields
    snmp_type?: 'v1' | 'v2c' | 'v3';
    community_string?: string;
    snmp_username?: string;
    auth_protocol?: string;
    privacy_protocol?: string;
    auth_password?: string;
    privacy_password?: string;

    // Firewall fields
    firewall_type?: 'cisco' | 'fortigate';
    hostname?: string;
    ssh_port?: number;
    https_port?: number;
    username?: string;
    password?: string;
    api_key?: string;

    // VMware fields
    vmware_type?: 'normal' | 'vcenter';
    vcenter_host?: string;
    port?: number;
    private_key?: string;
    make?: string;
    credential_data?: any;


    created?: string;
    updated?: string;
}

type ViewMode = 'list' | 'add' | 'edit';

const CredentialConfigPage: React.FC = () => {
    const { agentId } = useParams<{ agentId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [credentials, setCredentials] = useState<CredentialData[]>([]);
    const [selectedCredential, setSelectedCredential] = useState<CredentialData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [agent, setAgent] = useState<any>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [credentialToDelete, setCredentialToDelete] = useState<CredentialData | null>(null);
    const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
    const [credentialToToggle, setCredentialToToggle] = useState<CredentialData | null>(null);

    // Actions dropdown menu state
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: string | number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({ top: rect.bottom + 4, left: Math.max(10, rect.left - 110) });
        setOpenMenuId(prev => (prev === String(id) ? null : String(id)));
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (openMenuId && !target.closest(`[data-menu-id="${openMenuId}"]`) && !target.closest('[data-action-menu]')) {
                setOpenMenuId(null);
            }
        };
        const handleScroll = () => {
            if (openMenuId) setOpenMenuId(null);
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openMenuId]);

    // Refresh, Search, and Sort states
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const isFirstSearch = useRef(true);

    // Pagination states
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    const decodedToken = jwt_decode();
    const organizationId = decodedToken.organization_id;

    // Parse query params for agent info (passed from AgentTable)
    const queryParams = new URLSearchParams(location.search);
    const clientId = queryParams.get('client_id');
    const level = useClientLevel();
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (!hasPermission('credentials', 'read')) {
            showInfo("You don't have permission to access credentials. Please contact the administrator.");
            navigate('/agents');
            return;
        }
        if (agentId) {
            fetchCredentials(0, itemsPerPage);
            // In a real app, you might also fetch agent details here if not passed via props/query
            setAgent({ id: agentId, client_id: clientId });
        }
    }, [agentId, clientId]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return; // ⛔ skip initial load
        }

        navigate("/agents");
    }, [level]);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    // Re-fetch when debouncedSearch changes
    useEffect(() => {
        if (isFirstSearch.current) {
            isFirstSearch.current = false;
            return;
        }
        fetchCredentials(0, itemsPerPage, sortConfig, debouncedSearch);
    }, [debouncedSearch]);

    const fetchCredentials = async (
        skip: number = (currentPage - 1) * itemsPerPage,
        limit: number = itemsPerPage,
        sort: { key: string; direction: 'asc' | 'desc' } | null = sortConfig,
        search: string = debouncedSearch
    ) => {
        setIsLoading(true);

        const queryObj: any = {
            agent_id: agentId,
            organization_id: organizationId
        };

        if (search.trim()) {
            queryObj.$or = [
                { credential_name: { "$ilike": `%${search.trim()}%` } },
                { credential_type: { "$ilike": `%${search.trim()}%` } }
            ];
        }

        const params: any = {
            query: JSON.stringify(queryObj),
            skip: skip,
            limit: limit
        };

        if (sort) {
            params.sort_by = sort.key;
            params.sort_order = sort.direction;
        }

        try {
            const response = await baseapi.get('/credentials', { params });
            if (response.data.status) {
                setCredentials(response.data.data || []);
                setTotalPages(Math.ceil(response.data.total / limit));
                setTotalItems(response.data.total);
                const maxPage = Math.ceil(response.data.total / limit) || 1;
                if (currentPage > maxPage) {
                    setCurrentPage(maxPage);
                }
            } else {
                showError(response.data.message);
            }
        } catch (error) {
            console.error('Failed to fetch credentials:', error);
            showError('Failed to fetch credentials');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                const newDirection = prev.direction === 'asc' ? 'desc' : 'asc';
                const newConfig = { key, direction: newDirection as 'asc' | 'desc' };
                fetchCredentials((currentPage - 1) * itemsPerPage, itemsPerPage, newConfig, debouncedSearch);
                return newConfig;
            } else {
                const newConfig = { key, direction: 'asc' as 'asc' | 'desc' };
                fetchCredentials((currentPage - 1) * itemsPerPage, itemsPerPage, newConfig, debouncedSearch);
                return newConfig;
            }
        });
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        setCurrentPage(1);
        setSearchTerm('');
        setDebouncedSearch('');
        fetchCredentials(0, itemsPerPage, sortConfig, '');
        setTimeout(() => {
            setIsRefreshing(false);
        }, 1000);
    };

    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchCredentials((itemsPerPage * (page - 1)), itemsPerPage, sortConfig, debouncedSearch);
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchCredentials(0, value, sortConfig, debouncedSearch);
    };

    const handleAddNew = () => {
        setSelectedCredential(null);
        setViewMode('add');
    };

    const handleEdit = (credential: CredentialData) => {
        setSelectedCredential(credential);
        setViewMode('edit');
    };

    const handleDelete = (credential: CredentialData) => {
        setCredentialToDelete(credential);
        setIsDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!credentialToDelete) return;

        try {
            const response = await baseapi.delete(`/credentials/${credentialToDelete.id}`);
            if (response.data.status) {
                showSuccess('Credential deleted successfully');
                await fetchCredentials((itemsPerPage * (currentPage - 1)), itemsPerPage);
            } else {
                showError(response.data.message);
            }
        } catch (error) {
            console.error('Failed to delete credential:', error);
            showError('Failed to delete credential');
        } finally {
            setIsDeleteDialogOpen(false);
            setCredentialToDelete(null);
        }
    };

    const handleFormSuccess = async () => {
        await fetchCredentials();
        setViewMode('list');
        setSelectedCredential(null);
    };

    const handleStatusToggle = (credential: CredentialData) => {
        setCredentialToToggle(credential);
        setIsStatusDialogOpen(true);
    };

    const confirmStatusToggle = async () => {
        if (!credentialToToggle) return;

        try {
            const newState = !credentialToToggle.is_active;
            const response = await baseapi.put(`/credentials/${credentialToToggle.id}`, { is_active: newState });
            if (response.data.status) {
                showSuccess(`Credential ${newState ? 'enabled' : 'disabled'} successfully`);
                setCredentials(prev => prev.map(c => c.id === credentialToToggle.id ? { ...c, is_active: newState } : c));
            } else {
                showError(response.data.message);
            }
        } catch (error) {
            console.error('Failed to update status:', error);
            showError('Failed to update status');
        } finally {
            setIsStatusDialogOpen(false);
            setCredentialToToggle(null);
        }
    };

    const getCredentialTypeBadge = (credential: CredentialData) => {
        let label = credential.credential_type.toUpperCase();
        let colorClass = 'bg-gray-800 text-gray-400 border-gray-700';

        let parsedData: any = {};
        if (credential.credential_data) {
            try {
                parsedData = typeof credential.credential_data === 'string'
                    ? JSON.parse(credential.credential_data)
                    : credential.credential_data;
            } catch (e) {
                console.error('Failed to parse credential_data for badge:', e);
            }
        }

        if (credential.credential_type === 'snmp') {
            const snmpVersion = parsedData.snmp_version || credential.snmp_type;
            label = `SNMP ${snmpVersion?.toUpperCase() || ''}`;
            colorClass = 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
        } else if (credential.credential_type === 'firewall') {
            const firewallType = parsedData.firewall_type || credential.firewall_type;
            label = `${firewallType?.toUpperCase() || 'FIREWALL'}`;
            colorClass = 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20';
        } else if (credential.credential_type === 'vmware') {
            const vmwareType = parsedData.vmware_type || credential.vmware_type;
            label = `VMware ${vmwareType?.toUpperCase() || ''}`;
            colorClass = 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20';
        } else if (credential.credential_type === 'camera') {
            label = 'Camera';
            colorClass = 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20';
        }

        return (
            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${colorClass}`}>
                {label}
            </span>
        );
    };

    const renderCompactTable = () => (
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-gray-100 dark:bg-gray-800/80 sticky top-0 z-10">
                        <tr className="border-b border-gray-200 dark:border-gray-700/50">
                            <th
                                onClick={() => handleSort('credential_name')}
                                className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest cursor-pointer select-none"
                            >
                                <div className="flex items-center gap-1.5">
                                    <Key size={14} className="text-orange-500 dark:text-orange-400" />
                                    Name
                                    {sortConfig?.key === 'credential_name' ? (
                                        sortConfig.direction === 'asc' ? (
                                            <ChevronUp size={12} />
                                        ) : (
                                            <ChevronDown size={12} />
                                        )
                                    ) : (
                                        <ChevronDown size={12} className="opacity-40" />
                                    )}
                                </div>
                            </th>
                            <th
                                onClick={() => handleSort('credential_type')}
                                className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest cursor-pointer select-none"
                            >
                                <div className="flex items-center gap-1.5">
                                    <Shield size={14} className="text-blue-500 dark:text-blue-400" />
                                    Type
                                    {sortConfig?.key === 'credential_type' ? (
                                        sortConfig.direction === 'asc' ? (
                                            <ChevronUp size={12} />
                                        ) : (
                                            <ChevronDown size={12} />
                                        )
                                    ) : (
                                        <ChevronDown size={12} className="opacity-40" />
                                    )}
                                </div>
                            </th>
                            <th
                                onClick={() => handleSort('is_active')}
                                className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest cursor-pointer select-none"
                            >
                                <div className="flex items-center gap-1.5">
                                    <CheckCircle2 size={14} className="text-emerald-500 dark:text-emerald-400" />
                                    Status
                                    {sortConfig?.key === 'is_active' ? (
                                        sortConfig.direction === 'asc' ? (
                                            <ChevronUp size={12} />
                                        ) : (
                                            <ChevronDown size={12} />
                                        )
                                    ) : (
                                        <ChevronDown size={12} className="opacity-40" />
                                    )}
                                </div>
                            </th>
                            <th
                                onClick={() => handleSort('created')}
                                className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest cursor-pointer select-none"
                            >
                                <div className="flex items-center gap-1.5">
                                    <Calendar size={14} className="text-purple-500 dark:text-purple-400" />
                                    Created
                                    {sortConfig?.key === 'created' ? (
                                        sortConfig.direction === 'asc' ? (
                                            <ChevronUp size={12} />
                                        ) : (
                                            <ChevronDown size={12} />
                                        )
                                    ) : (
                                        <ChevronDown size={12} className="opacity-40" />
                                    )}
                                </div>
                            </th>
                            <th
                                onClick={() => handleSort('updated')}
                                className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest cursor-pointer select-none"
                            >
                                <div className="flex items-center gap-1.5">
                                    <Calendar size={14} className="text-purple-500 dark:text-purple-400" />
                                    Updated
                                    {sortConfig?.key === 'updated' ? (
                                        sortConfig.direction === 'asc' ? (
                                            <ChevronUp size={12} />
                                        ) : (
                                            <ChevronDown size={12} />
                                        )
                                    ) : (
                                        <ChevronDown size={12} className="opacity-40" />
                                    )}
                                </div>
                            </th>
                            <th className="px-3 py-1.5 text-[10px] font-bold text-gray-700 dark:text-gray-300 uppercase tracking-widest text-center select-none">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
                        {credentials.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="py-16 text-center text-gray-500 dark:text-gray-300 font-bold uppercase tracking-widest italic">
                                    No credentials configured
                                </td>
                            </tr>
                        ) : (
                            credentials.map((cred) => (
                                <tr key={cred.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group border-b border-gray-200 dark:border-gray-700/30">
                                    <td className="px-3 py-2">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-gray-900 dark:text-gray-200 text-[11px] font-bold leading-tight">{cred.credential_name}</span>
                                        </div>
                                    </td>
                                    <td className="px-3 py-2">
                                        {getCredentialTypeBadge(cred)}
                                    </td>
                                    <td className="px-3 py-2">
                                        <button
                                            onClick={() => handleStatusToggle(cred)}
                                            className="flex items-center gap-2 group transition-all hover:opacity-80"
                                            title={cred.is_active ? 'Disable Credential' : 'Enable Credential'}
                                        >
                                            <div className={`
                                                relative w-8 h-4 rounded-full transition-all duration-300 ease-in-out border
                                                ${cred.is_active
                                                    ? 'bg-emerald-600 border-emerald-500/30'
                                                    : 'bg-rose-600 border-rose-500/30'}
                                            `}>
                                                <div className={`
                                                    absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-all duration-300 ease-in-out
                                                    ${cred.is_active ? 'left-4.5' : 'left-0.5'}
                                                `} />
                                            </div>
                                            <span className={`text-[10px] font-bold uppercase tracking-wider ${cred.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                {cred.is_active ? 'Active' : 'Disabled'}
                                            </span>
                                        </button>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className="text-gray-700 dark:text-gray-100 text-[10px] font-mono flex items-center gap-1.5">
                                            {cred.created
                                                ? new Date(cred.created.slice(0, 23) + "Z").toLocaleString()
                                                : "-"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span className="text-gray-600 dark:text-gray-300 text-[11px] font-mono flex items-center gap-1">
                                            {cred.updated
                                                ? new Date(cred.updated.slice(0, 23) + "Z").toLocaleString()
                                                : "-"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <button
                                            data-menu-id={cred.id}
                                            onClick={(e) => handleMenuOpen(e, cred.id!)}
                                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                                        >
                                            <CircleEllipsis className="w-4.5 h-4.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Footer */}
            <div className="bg-gray-50 dark:bg-gray-800/80 border-t border-gray-200 dark:border-gray-700/50 px-3 py-1.5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <ItemsPerPageDropdown
                        value={itemsPerPage}
                        onChange={(val: any) => handleItemsPerPageChange(val)}
                    />
                    <span className="text-[10px] text-gray-600 dark:text-gray-300 font-bold uppercase tracking-widest leading-none">
                        <span className="text-gray-900 dark:text-gray-200">{startIndex + 1}</span> - <span className="text-gray-900 dark:text-gray-200">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                        title="First Page"
                    >
                        <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                        title="Previous Page"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="min-w-[24px] h-[24px] flex items-center justify-center bg-orange-600 rounded text-white text-[11px] font-bold shadow-lg shadow-orange-900/20 mx-1">
                        {currentPage}
                    </div>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                        title="Next Page"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                        title="Last Page"
                    >
                        <ChevronsRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-gray-50 dark:bg-gray-900 p-3 min-h-screen pb-20 text-gray-900 dark:text-gray-100">
            <div className="w-full mx-auto">
                {/* Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/agents')}
                            className="p-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded text-gray-700 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-all shadow-sm"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-1.5">
                            <div className="bg-orange-600 p-1.5 rounded shadow-lg">
                                <Key className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h1 className="font-bold text-lg text-gray-900 dark:text-gray-100 leading-none">Credential Config</h1>
                            </div>
                            {!isLoading && (
                                <div className="font-bold text-lg text-gray-900 dark:text-gray-100">
                                    ({totalItems})
                                </div>
                            )}
                        </div>
                    </div>

                    {viewMode === 'list' && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                className="rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-sm"
                                title="Refresh Credentials"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                            </button>

                            <div className="relative w-full sm:w-56">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-400 w-3.5 h-3.5" />
                                <input
                                    type="text"
                                    placeholder="Search credentials..."
                                    className="w-full bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-orange-500 outline-none transition-all shadow-sm"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>

                            <button
                                onClick={handleAddNew}
                                className="flex items-center justify-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg text-[11px] font-bold shadow-lg shadow-orange-900/20 transition-all active:scale-95 uppercase tracking-wider"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add Credential
                            </button>
                        </div>
                    )}
                </div>

                {/* Main Content Area */}
                <div className="relative">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-32 space-y-4 bg-white/50 dark:bg-gray-800/20 border border-gray-200 dark:border-gray-800 rounded-b-xl">
                            <div className="relative">
                                <Loader className="w-10 h-10 text-orange-500 animate-spin" />
                                <div className="absolute inset-0 bg-orange-500/10 blur-xl rounded-full"></div>
                            </div>
                            <span className="text-gray-600 dark:text-gray-300 text-[10px] font-bold uppercase tracking-widest italic">Syncing configurations...</span>
                        </div>
                    ) : (
                        <>
                            {viewMode === 'list' ? (
                                renderCompactTable()
                            ) : (
                                <div className="max-w-3xl mx-auto my-8 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl   overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                                    <CredentialForm
                                        agent={agent}
                                        organizationId={organizationId}
                                        credential={selectedCredential}
                                        isEdit={viewMode === 'edit'}
                                        onSuccess={handleFormSuccess}
                                        onBack={() => setViewMode('list')}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Custom Delete Confirmation Modal */}
            <Modal open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                            w-[95%] sm:w-[450px] bg-white dark:bg-gray-900 border border-red-500/30 dark:border-red-500/20   p-6 rounded-2xl text-gray-900 dark:text-white
                            relative overflow-hidden outline-none">

                    {/* Top Accent Line */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-red-500/50 to-transparent"></div>

                    <div className="flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4 border border-red-500/20">
                            <Trash2 className="w-8 h-8 text-red-500" />
                        </div>

                        <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">Delete Credential</h2>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                            Are you sure you want to delete <span className="text-gray-900 dark:text-gray-200 font-semibold">"{credentialToDelete?.credential_name}"</span>?
                            This action cannot be undone.
                        </p>

                        <div className="flex w-full gap-3">
                            <button
                                onClick={() => setIsDeleteDialogOpen(false)}
                                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl transition-all font-bold text-xs uppercase tracking-widest border border-gray-300 dark:border-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all font-bold text-xs uppercase tracking-widest shadow-lg shadow-red-900/20"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Custom Status Toggle Confirmation Modal */}
            <Modal open={isStatusDialogOpen} onClose={() => setIsStatusDialogOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                            w-[95%] sm:w-[450px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700   p-6 rounded-2xl text-gray-900 dark:text-white
                            relative overflow-hidden outline-none">

                    <div className="flex flex-col items-center text-center">
                        <div className={`w-16 h-16 ${credentialToToggle?.is_active ? 'bg-rose-500/10 border-rose-500/20' : 'bg-emerald-500/10 border-emerald-500/20'} rounded-full flex items-center justify-center mb-4 border`}>
                            <AlertTriangle className={`w-8 h-8 ${credentialToToggle?.is_active ? 'text-rose-500' : 'text-emerald-500'}`} />
                        </div>

                        <h2 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                            {credentialToToggle?.is_active ? 'Disable Credential' : 'Enable Credential'}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
                            Are you sure you want to {credentialToToggle?.is_active ? 'disable' : 'enable'} <span className="text-gray-900 dark:text-gray-200 font-semibold">"{credentialToToggle?.credential_name}"</span>?
                        </p>

                        <div className="flex w-full gap-3">
                            <button
                                onClick={() => setIsStatusDialogOpen(false)}
                                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl transition-all font-bold text-xs uppercase tracking-widest border border-gray-300 dark:border-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmStatusToggle}
                                className={`flex-1 px-4 py-2.5 ${credentialToToggle?.is_active ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'} text-white rounded-xl transition-all font-bold text-xs uppercase tracking-widest shadow-lg`}
                            >
                                {credentialToToggle?.is_active ? 'Disable' : 'Enable'}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Actions Portal Menu */}
            {openMenuId && createPortal(
                (() => {
                    const activeCred = credentials.find(c => String(c.id) === String(openMenuId));
                    if (!activeCred) return null;
                    return (
                        <div
                            data-action-menu
                            className="fixed w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl   z-[9999] overflow-hidden py-1.5"
                            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                        >
                            <button
                                onClick={() => {
                                    handleEdit(activeCred);
                                    setOpenMenuId(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 transition-colors cursor-pointer"
                            >
                                <Pencil className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" /> Edit Credential
                            </button>
                            <button
                                onClick={() => {
                                    handleDelete(activeCred);
                                    setOpenMenuId(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-[11px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-3 font-bold cursor-pointer"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete Credential
                            </button>
                        </div>
                    );
                })(),
                document.body
            )}
        </div>
    );
};

export default CredentialConfigPage;
